import React from 'react';

import { FiPaperclip } from 'react-icons/fi';

import {
	ModalContainer,
	Select,
	InputField,
	TextArea,
	UploadFiles,
	InputDate,
	SideSelect,
} from '@welbe/web-design-system';

import { Button } from '@welbe/design-system';

import {
	Container,
	Icon,
	Title,
	ButtonContainer,
	ContentContainer,
	LeftContent,
	RightContent,
	UploadContainer,
	SuperscriptionContainer,
	Superscription,
	RightButtons,
	LeftButtons,
} from './ModalCommunication.styles';

import { useModalCommunication } from './ModalCommunication.hooks';

import EmailLetter from 'App/assets/img/email.svg?url';
import { timeOptions } from './utils/timeOptions';
import { ModalConfirm } from 'App/Components/ModalConfirm/ModalConfirm';

import { superscriptionItems } from 'Views/Campaigns/components/NewCampaign/NewCampaign.enums';

import { Campaign, Communication } from 'types';
import { ModalPreviewEmailCommunication } from 'Views/Campaigns/components/ModalPreviewEmailCommunication/ModalPreviewEmailCommunication';
import { PreviewEmailTypeEnum } from 'Views/Campaigns/enum/previewEmailType.enum';
import { PreviewEmailType } from 'Views/Campaigns/constants/previewEmailType';
import Analytics from 'helpers/analytics';
import {
	dateScheduled,
	FormattedDatesResult,
	getFormattedDatesSchedules,
} from 'utils/formatterDatesScheduled';

type ModalCommunicationProps = {
	onClose: () => void;
	visible: boolean;
	campaign: Campaign;
	communication?: Communication;
	communicationCreatedTypes?: string[];
	communicationCreatedDates?: dateScheduled[];
	initialCommunicationType?: any;
	optionsCommunicationType?: any[];
};

export const ModalCommunication = ({
	onClose,
	visible,
	campaign,
	communication,
	communicationCreatedTypes,
	communicationCreatedDates,
	initialCommunicationType,
	optionsCommunicationType = [],
}: ModalCommunicationProps): JSX.Element => {
	const {
		isLoading,
		isLoadingCommunicationType,
		communicationTypeOptions,
		communicationForm,
		errorForm,
		showModalConfirm,
		showModalPreviewEmailCommunication,
		verifyPermissionToDeleteCommunication,
		createHeightModalResponsive,
		handleConfirm,
		handleSeleteCommunicationType,
		handleNameCommunication,
		handleTitleCommunication,
		handleMessageCommunication,
		handleFileCommunication,
		handleDateCommunication,
		handleTimeCommunication,
		handleCloseModalConfirm,
		handleDeleteCommunication,
		handlePreviewEmailCommunication,
		handleClosePreviewEmailCommunication,
		handleChangePreviewType,
	} = useModalCommunication({
		campaign,
		onClose,
		communication,
		initialCommunicationType,
	});

	const handleClose = () => {
		Analytics.track('campaign', 'close-add-communication-modal');
		onClose();
	};

	const formattedDatesScheduled: FormattedDatesResult =
		communicationCreatedDates
			? getFormattedDatesSchedules(communicationCreatedDates)
			: undefined;

	return (
		<>
			<ModalContainer
				visible={visible}
				height={createHeightModalResponsive()}
				zIndex={1200}
				closeOnlyOnTheButton
				hasCloseButton
				onClose={handleClose}
				headerIcon={<Icon src={EmailLetter} />}
				width={'55vw'}
			>
				<Container>
					<Title>
						{communicationForm.id
							? 'Editar comunicación'
							: 'Crear comunicación'}
					</Title>
					<ContentContainer>
						<LeftContent>
							<Select
								id="communicationType"
								loading={isLoadingCommunicationType}
								placeholder="Seleciona una opción"
								label="Tipo de comunicación"
								options={
									optionsCommunicationType.length
										? optionsCommunicationType
										: communicationTypeOptions
								}
								onChange={handleSeleteCommunicationType}
								initialSelected={
									initialCommunicationType?.id
										? initialCommunicationType
										: communicationForm.communicationType
								}
								invalidInput={!!errorForm.communicationType}
								errorMessage={errorForm.communicationType}
								disabled={isLoading || !!communicationForm.id}
							/>
							<InputField
								id="communicationName"
								placeholder="Escribe el nombren"
								disabled={isLoading}
								label="Nombre de la comunicación"
								onChange={handleNameCommunication}
								value={communicationForm.name}
								error={!!errorForm.name}
								errorText={errorForm.name}
							/>
							<InputField
								id="communicationTitle"
								placeholder="Título de la comunicación"
								label="Título"
								disabled={isLoading}
								onChange={handleTitleCommunication}
								value={communicationForm.title}
								error={!!errorForm.title}
								errorText={errorForm.title}
							/>
							<Select
								id="communicationPreviewType"
								label="Tipo de plantilla de la communication"
								onChange={handleChangePreviewType}
								disabled={isLoading}
								initialSelected={communicationForm?.previewType}
								options={[
									{
										id: PreviewEmailTypeEnum.DEFAULT,
										value: PreviewEmailType[PreviewEmailTypeEnum.DEFAULT],
									},
									{
										id: PreviewEmailTypeEnum.CUSTOM,
										value: PreviewEmailType[PreviewEmailTypeEnum.CUSTOM],
									},
								]}
							/>
							<TextArea
								id="communicationMessage"
								label="Mensaje"
								handleChange={handleMessageCommunication}
								value={communicationForm.message}
								rows={7}
								error={!!errorForm.message}
								errorMessage={errorForm.message}
								disabled={
									isLoading ||
									communicationForm?.previewType.id ===
										PreviewEmailTypeEnum.DEFAULT
								}
								placeholder={
									communicationForm?.previewType.id ===
									PreviewEmailTypeEnum.DEFAULT
										? 'Plantilla patrón elegida.'
										: 'Mensaje'
								}
							/>
							<UploadContainer>
								<UploadFiles
									id="communicationFile"
									width="100%"
									iconOnly
									deleteFile
									disabled={isLoading}
									icon={<FiPaperclip style={{ transform: 'rotate(315deg)' }} />}
									acceptedFileTypes={[
										'image/jpeg',
										'image/png',
										'application/pdf',
									]}
									onHandleFile={handleFileCommunication}
									fileName={communicationForm.fileName}
								/>
							</UploadContainer>
						</LeftContent>
						<RightContent>
							<SideSelect
								id="communicationTime"
								label="Hora de envío"
								disabled={isLoading}
								onChange={handleTimeCommunication}
								options={timeOptions}
								value={communicationForm.time}
								error={!!errorForm.time}
								errorMessage={errorForm.time}
							/>
							<InputDate
								id="communicationDate"
								label="Fecha de envío"
								disabled={isLoading}
								onChange={(value) => {
									if (value) {
										handleDateCommunication(value);
									}
								}}
								value={
									communicationForm?.date ? communicationForm?.date : undefined
								}
								minDate={new Date()}
								error={!!errorForm.date}
								errorText={errorForm.date}
								scheduledDates={formattedDatesScheduled}
								isOpen
							/>

							<SuperscriptionContainer>
								{superscriptionItems.map((item: any) => {
									return (
										<React.Fragment key={item.name}>
											{communicationCreatedTypes?.includes(item.type) && (
												<Superscription color={item.color}>
													{item.name}
												</Superscription>
											)}
										</React.Fragment>
									);
								})}
							</SuperscriptionContainer>
						</RightContent>
					</ContentContainer>

					<ButtonContainer>
						<LeftButtons>
							<Button
								id="communicationPreviewEmailButton"
								width="170px"
								onClick={handlePreviewEmailCommunication}
								disabled={
									isLoading ||
									(!communication?.communicationType?.id &&
										!communicationForm?.communicationType?.id) ||
									(communicationForm.previewType.id ===
										PreviewEmailTypeEnum.CUSTOM &&
										!communicationForm.message)
								}
							>
								Previsualizar
							</Button>
						</LeftButtons>
						<RightButtons>
							{communication?.id && (
								<Button
									id="communicationDeleteButton"
									variant="outline"
									width="130px"
									colorPalette="red"
									onClick={handleDeleteCommunication}
									disabled={
										isLoading || verifyPermissionToDeleteCommunication()
									}
								>
									Eliminar
								</Button>
							)}
							<Button
								id="communicationCancelButton"
								variant="outline"
								width="130px"
								onClick={handleClose}
								disabled={isLoading}
							>
								Cancelar
							</Button>
							<Button
								id="communicationConfirmButton"
								variant="solid"
								width="130px"
								loading={isLoading}
								onClick={handleConfirm}
								disabled={isLoading}
							>
								Guardar
							</Button>
						</RightButtons>
					</ButtonContainer>
				</Container>
			</ModalContainer>
			<ModalConfirm
				visible={showModalConfirm}
				onClose={handleCloseModalConfirm}
				message={'Comunicación programada con éxito.'}
			/>
			{showModalPreviewEmailCommunication && (
				<ModalPreviewEmailCommunication
					campaign={{
						id: campaign.id,
						communication: {
							message: communicationForm.message,
							type: communicationForm.communicationType.id,
							useCustomTemplate:
								communicationForm.previewType.id === PreviewEmailTypeEnum.CUSTOM
									? true
									: false,
						},
					}}
					onClose={handleClosePreviewEmailCommunication}
				></ModalPreviewEmailCommunication>
			)}
		</>
	);
};
